import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '../App';
import { Sparkles, Eye, Users, Link2, Brain, Bookmark, ChevronRight, Trophy } from 'lucide-react';
import { Progress } from '../components/ui/progress';
import { Button } from '../components/ui/button';

const tierIcons = {
  NEWCOMER: '🌱',
  CURIOUS: '🔍',
  SEEKER: '🧭',
  AWAKENED: '✨',
  ENLIGHTENED: '🌟'
};

export default function WowMeter({ compact = false }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }

    const fetchProgress = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/progress/stats`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          withCredentials: true
        });
        setProgress(res.data);
      } catch (error) {
        console.error('Error fetching progress:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProgress();
  }, [user]);

  if (!user) {
    return (
      <div className="card-dark p-6 text-center" data-testid="wow-meter-guest">
        <Sparkles className="w-10 h-10 mx-auto mb-4 text-cyan-400" />
        <h3 className="font-heading text-lg font-semibold mb-2">Theory of Wow Meter</h3>
        <p className="text-white/50 text-sm mb-4">Sign in to track your exploration and discover the whole picture.</p>
        <Button onClick={() => navigate('/auth')} className="btn-outline text-xs">
          Sign In to Start
        </Button>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="card-dark p-6">
        <div className="skeleton h-24 rounded-sm" />
      </div>
    );
  }

  if (!progress) return null;

  const progressToNext = progress.next_tier.required 
    ? Math.min((progress.wow_score / progress.next_tier.required) * 100, 100)
    : 100;

  if (compact) {
    return (
      <div 
        className="card-dark p-4 cursor-pointer group" 
        onClick={() => navigate('/profile')}
        data-testid="wow-meter-compact"
      >
        <div className="flex items-center gap-4">
          <div 
            className="w-12 h-12 rounded-full flex items-center justify-center text-2xl"
            style={{ backgroundColor: `${progress.tier_color}20`, borderColor: progress.tier_color, borderWidth: 2 }}
          >
            {tierIcons[progress.tier]}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="font-mono text-xs uppercase tracking-wider" style={{ color: progress.tier_color }}>
                {progress.tier}
              </span>
              <span className="text-white/40 text-xs">{progress.wow_score} pts</span>
            </div>
            <Progress value={progressToNext} className="h-1.5" />
          </div>
          <ChevronRight className="w-5 h-5 text-white/30 group-hover:text-white/60 transition-colors" />
        </div>
      </div>
    );
  }

  return (
    <div className="card-dark p-6" data-testid="wow-meter-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Trophy className="w-6 h-6 text-cyan-400" />
          <h3 className="font-heading text-xl font-semibold">Theory of Wow Meter</h3>
        </div>
        <div 
          className="px-3 py-1 rounded-sm font-mono text-sm uppercase tracking-wider"
          style={{ backgroundColor: `${progress.tier_color}20`, color: progress.tier_color }}
        >
          {tierIcons[progress.tier]} {progress.tier}
        </div>
      </div>

      {/* Score */}
      <div className="text-center mb-6">
        <p className="text-5xl font-bold mb-2" style={{ color: progress.tier_color }}>
          {progress.wow_score}
        </p>
        <p className="text-white/40 text-sm font-mono">WOW POINTS</p>
      </div>

      {/* Progress to next tier */}
      {progress.next_tier.next && (
        <div className="mb-6">
          <div className="flex items-center justify-between text-sm mb-2">
            <span className="text-white/50">Progress to {progress.next_tier.next}</span>
            <span className="text-white/70">{progress.wow_score} / {progress.next_tier.required}</span>
          </div>
          <Progress value={progressToNext} className="h-2" />
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <StatCard
          icon={Eye}
          label="Stories"
          value={progress.stats.stories_viewed}
          total={progress.stats.stories_total}
          percentage={progress.stats.stories_percentage}
        />
        <StatCard
          icon={Users}
          label="People"
          value={progress.stats.people_viewed}
          total={progress.stats.people_total}
          percentage={progress.stats.people_percentage}
        />
        <StatCard
          icon={Link2}
          label="Connections"
          value={progress.stats.connections_discovered}
          total={progress.stats.connections_total}
          percentage={progress.stats.connections_percentage}
        />
        <StatCard
          icon={Brain}
          label="AI Questions"
          value={progress.stats.ai_questions}
        />
      </div>

      {/* Bookmarks */}
      <div className="flex items-center justify-between py-3 border-t border-white/10">
        <div className="flex items-center gap-2 text-white/50">
          <Bookmark className="w-4 h-4" />
          <span className="text-sm">Saved Items</span>
        </div>
        <span className="text-white font-mono">{progress.stats.bookmarks}</span>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, total, percentage }) {
  return (
    <div className="bg-white/5 rounded-sm p-3">
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-4 h-4 text-cyan-400" />
        <span className="text-white/50 text-xs uppercase tracking-wider">{label}</span>
      </div>
      <div className="flex items-baseline gap-1">
        <span className="text-xl font-bold text-white">{value}</span>
        {total !== undefined && (
          <span className="text-white/30 text-sm">/ {total}</span>
        )}
      </div>
      {percentage !== undefined && (
        <Progress value={percentage} className="h-1 mt-2" />
      )}
    </div>
  );
}
